import type { ButtonHTMLAttributes, HTMLAttributes, ReactNode } from 'react';
import { uiTexts } from './texts';

export interface ChipProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'onClick'> {
  selected?: boolean;
  size?: 'sm' | 'md';
  icon?: ReactNode;
  onClick?: () => void;
  /** Adds a × button that calls this (a picked value in a `MultiSelect`, a filter to clear). */
  onRemove?: () => void;
  /** Accessible name of the × button; default `uiTexts.remove(<children>)` when the children are text. */
  removeLabel?: string;
}

/**
 * A pill: static (span) without onClick, a toggle button (`aria-pressed` from `selected`) with it; `onRemove` adds a
 * remove button, so the chip itself stays a span.
 */
export function Chip({
  selected,
  size = 'md',
  icon,
  onClick,
  onRemove,
  removeLabel,
  className,
  children,
  disabled,
  ...rest
}: ChipProps) {
  const cls = [
    'nyk-ui-chip',
    selected ? 'nyk-ui-selected' : '',
    size === 'sm' ? 'nyk-ui-chip-sm' : '',
    onClick && !onRemove ? 'nyk-ui-clickable' : '',
    onRemove ? 'nyk-ui-chip-removable' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');
  if (onRemove) {
    const text = typeof children === 'string' || typeof children === 'number' ? String(children) : '';
    return (
      <span className={cls} {...(rest as HTMLAttributes<HTMLSpanElement>)}>
        {icon}
        <span className="nyk-ui-chip-text">{children}</span>
        <button
          type="button"
          className="nyk-ui-chip-remove"
          aria-label={removeLabel ?? uiTexts.remove(text)}
          disabled={disabled}
          onClick={onRemove}
        >
          ×
        </button>
      </span>
    );
  }
  if (!onClick) {
    return (
      <span className={cls} {...(rest as HTMLAttributes<HTMLSpanElement>)}>
        {icon}
        {children}
      </span>
    );
  }
  return (
    <button
      type="button"
      className={cls}
      onClick={onClick}
      disabled={disabled}
      aria-pressed={selected}
      {...rest}
    >
      {icon}
      {children}
    </button>
  );
}
